import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import "./styles/app.css";
import appConfig from "./config/appConfig";
import soundcloudConfig from "./config/soundcloudConfig";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import PlayerBar from "./components/PlayerBar";
import GalaxyBackground from "./components/ui/GalaxyBackground";
import ComingSoonOverlay from "./components/ui/ComingSoonOverlay";
import HomeView from "./views/HomeView";
import DiscoveryView from "./views/DiscoveryView";
import LibraryView from "./views/LibraryView";
import QueueView from "./views/QueueView";
import AboutView from "./views/AboutView";
import SettingsView from "./views/SettingsView";
import EnvMissingView from "./views/EnvMissingView";
import useHomeFeed from "./hooks/useHomeFeed";
import useDiscoveryFeed from "./hooks/useDiscoveryFeed";
import { PlayerProvider, usePlayer } from "./contexts/PlayerContext";
import { DEFAULT_THEME_SETTINGS, getThemePresetById } from "./config/themeConfig";
import {
  loadThemeSettings,
  resetThemeSettings,
  sanitizeThemeSettings,
  saveThemeSettings,
} from "./services/ui/themeSettingsStorage";
import {
  navItems,
  playlists,
  relatedTracks,
} from "./data/mockData";

const viewMeta = {
  home: { title: "Home", subtitle: "Your galaxy soundtrack" },
  discovery: { title: "Discovery", subtitle: "Find new sounds" },
  library: { title: "Library", subtitle: "Your collections" },
  queue: { title: "Queue", subtitle: "Now playing line" },
  about: { title: "About", subtitle: "Product information" },
  settings: { title: "Settings", subtitle: "Customize your experience" },
};

const comingSoonViews = new Set(["library"]);

function AppContent() {
  const [activeView, setActiveView] = useState("home");
  const [homeSearchInput, setHomeSearchInput] = useState("");
  const [homeSearchQuery, setHomeSearchQuery] = useState("");
  const [themeSettings, setThemeSettings] = useState(() => loadThemeSettings());
  const { playTrack } = usePlayer();
  const {
    data: homeData,
    isLoading: isHomeLoading,
    isLoadingMore: isHomeLoadingMore,
    loadingCollection: homeLoadingCollection,
    error: homeError,
    refresh: refreshHome,
    loadMoreSearch,
    loadMoreCollection,
  } = useHomeFeed({
    query: homeSearchQuery,
    enabled: activeView === "home",
  });
  const {
    data: discoveryData,
    isLoading: isDiscoveryLoading,
    error: discoveryError,
    refresh: refreshDiscovery,
    selectedGenreId,
    activeReleases,
    selectGenre,
    openPlaylist,
    selectedPlaylist,
    playlistTracks,
    isLoadingPlaylist,
    playlistError,
  } = useDiscoveryFeed({
    enabled: activeView === "discovery",
  });
  const loadingToastIdRef = useRef("");
  const previousSearchQueryRef = useRef("");
  const previousLoadingRef = useRef(false);
  const lastSearchLoadTypeRef = useRef("tracks");

  useEffect(() => {
    const viewTitle = viewMeta[activeView]?.title || "Home";
    document.title = viewTitle
      ? `${viewTitle} | ${appConfig.appName}`
      : appConfig.defaultTitle;
  }, [activeView]);

  useEffect(() => {
    saveThemeSettings(themeSettings);
    const root = document.documentElement;
    const body = document.body;
    const preset = getThemePresetById(themeSettings.presetId);

    root.style.setProperty("--bg-linear-start", preset.colors.bgLinearStart);
    root.style.setProperty("--bg-linear-mid", preset.colors.bgLinearMid);
    root.style.setProperty("--bg-linear-end", preset.colors.bgLinearEnd);
    root.style.setProperty("--bg-nebula-a", preset.colors.nebulaA);
    root.style.setProperty("--bg-nebula-b", preset.colors.nebulaB);
    root.style.setProperty("--bg-nebula-c", preset.colors.nebulaC);
    root.style.setProperty("--neon-cyan", preset.colors.neonCyan);
    root.style.setProperty("--neon-purple", preset.colors.neonPurple);
    root.style.setProperty("--neon-pink", preset.colors.neonPink);

    body.classList.remove("cursor-music", "cursor-default", "cursor-neon");
    body.classList.add(`cursor-${themeSettings.cursorMode}`);
  }, [themeSettings]);

  useEffect(() => {
    if (activeView !== "home") return;

    const isBusy = isHomeLoading || isHomeLoadingMore;
    if (isBusy && !loadingToastIdRef.current) {
      loadingToastIdRef.current = toast.loading("Loading music data...");
    }

    if (!isBusy && loadingToastIdRef.current) {
      toast.dismiss(loadingToastIdRef.current);
      loadingToastIdRef.current = "";
    }

    if (homeError) {
      toast.error("Failed to load data. Please retry.");
    }
  }, [activeView, homeError, isHomeLoading, isHomeLoadingMore]);

  useEffect(() => {
    if (homeLoadingCollection) {
      lastSearchLoadTypeRef.current = homeLoadingCollection;
      return;
    }

    if (isHomeLoading && homeSearchQuery) {
      lastSearchLoadTypeRef.current = "tracks";
    }
  }, [homeLoadingCollection, homeSearchQuery, isHomeLoading]);

  useEffect(() => {
    const justFinishedSearchLoad =
      activeView === "home" &&
      homeSearchQuery &&
      previousLoadingRef.current &&
      !isHomeLoading &&
      !isHomeLoadingMore;

    if (justFinishedSearchLoad && previousSearchQueryRef.current === homeSearchQuery) {
      const type = lastSearchLoadTypeRef.current;
      const metaByType = {
        tracks: {
          count: homeData.recommendedCards.length,
          label: "track",
        },
        users: {
          count: homeData.searchCollections.users.length,
          label: "user",
        },
        playlists: {
          count: homeData.searchCollections.playlists.length,
          label: "playlist",
        },
        albums: {
          count: homeData.searchCollections.albums.length,
          label: "album",
        },
      };
      const selected = metaByType[type] || metaByType.tracks;
      const plural = selected.count === 1 ? "" : "s";
      toast.success(`Loaded ${selected.count} ${selected.label} result${plural}`);
    }

    previousLoadingRef.current = isHomeLoading || isHomeLoadingMore;
  }, [
    activeView,
    homeData.recommendedCards.length,
    homeData.searchCollections.albums.length,
    homeData.searchCollections.playlists.length,
    homeData.searchCollections.users.length,
    homeSearchQuery,
    isHomeLoading,
    isHomeLoadingMore,
  ]);

  function handleHomeSearchSubmit(value) {
    setHomeSearchQuery(value);
    previousSearchQueryRef.current = value;
    if (value) {
      toast(`Searching for "${value}"...`, { icon: "🔎" });
    } else {
      toast("Showing trending tracks", { icon: "🎵" });
    }
  }

  const handlePlayTrack = useCallback((track, queueSource = [], queueIndex = 0) => {
    playTrack(track, queueSource, queueIndex);
  }, [playTrack]);

  const handleThemeChange = useCallback((patch) => {
    setThemeSettings((previous) => sanitizeThemeSettings({ ...previous, ...patch }));
  }, []);

  const handleThemeReset = useCallback(() => {
    resetThemeSettings();
    setThemeSettings(DEFAULT_THEME_SETTINGS);
    toast.success("Theme reset to default");
  }, []);

  const currentView = useMemo(() => {
    switch (activeView) {
      case "discovery":
        return (
          <DiscoveryView
            genres={discoveryData.genres}
            chartSelections={discoveryData.chartSelections}
            selectedGenreId={selectedGenreId}
            activeReleases={activeReleases}
            onSelectGenre={selectGenre}
            onOpenPlaylist={openPlaylist}
            selectedPlaylist={selectedPlaylist}
            playlistTracks={playlistTracks}
            isLoadingPlaylist={isLoadingPlaylist}
            playlistError={playlistError}
            newReleases={discoveryData.newReleases}
            recommendedCards={discoveryData.recommendedCards}
            isLoading={isDiscoveryLoading}
            error={discoveryError}
            onRetry={refreshDiscovery}
            onPlayTrack={handlePlayTrack}
          />
        );
      case "library":
        return <LibraryView playlists={playlists} relatedTracks={relatedTracks} />;
      case "queue":
        return <QueueView />;
      case "about":
        return <AboutView />;
      case "settings":
        return (
          <SettingsView
            settings={themeSettings}
            onChange={handleThemeChange}
            onReset={handleThemeReset}
          />
        );
      default:
        return (
          <HomeView
            heroAlbum={homeData.heroAlbum}
            recommendedCards={homeData.recommendedCards}
            relatedTracks={homeData.relatedTracks}
            searchCollections={homeData.searchCollections}
            onPlayTrack={handlePlayTrack}
            searchQuery={homeSearchQuery}
            isLoading={isHomeLoading}
            isLoadingMore={isHomeLoadingMore}
            error={homeError}
            onRetry={refreshHome}
            onLoadMoreSearch={loadMoreSearch}
            hasMoreSearch={homeData.pagination.hasMore}
            totalResults={homeData.pagination.total}
            totalUsers={homeData.pagination.collections.users.total}
            totalPlaylists={homeData.pagination.collections.playlists.total}
            totalAlbums={homeData.pagination.collections.albums.total}
            hasMoreUsers={homeData.pagination.collections.users.hasMore}
            hasMorePlaylists={homeData.pagination.collections.playlists.hasMore}
            hasMoreAlbums={homeData.pagination.collections.albums.hasMore}
            onLoadMoreUsers={() => loadMoreCollection("users")}
            onLoadMorePlaylists={() => loadMoreCollection("playlists")}
            onLoadMoreAlbums={() => loadMoreCollection("albums")}
            loadingCollection={homeLoadingCollection}
          />
        );
    }
  }, [
    activeView,
    homeData.heroAlbum,
    homeData.pagination.hasMore,
    homeData.pagination.collections.albums.hasMore,
    homeData.pagination.collections.albums.total,
    homeData.pagination.collections.playlists.hasMore,
    homeData.pagination.collections.playlists.total,
    homeData.pagination.collections.users.hasMore,
    homeData.pagination.collections.users.total,
    homeData.pagination.total,
    homeData.recommendedCards,
    homeData.searchCollections,
    homeData.relatedTracks,
    discoveryData.genres,
    discoveryData.chartSelections,
    discoveryData.newReleases,
    discoveryData.recommendedCards,
    selectedGenreId,
    activeReleases,
    selectGenre,
    openPlaylist,
    selectedPlaylist,
    playlistTracks,
    isLoadingPlaylist,
    playlistError,
    isDiscoveryLoading,
    discoveryError,
    refreshDiscovery,
    themeSettings,
    handleThemeChange,
    handleThemeReset,
    homeError,
    homeSearchQuery,
    isHomeLoading,
    isHomeLoadingMore,
    homeLoadingCollection,
    handlePlayTrack,
    loadMoreCollection,
    loadMoreSearch,
    refreshHome,
  ]);

  return (
    <div className="app-root">
      <GalaxyBackground settings={themeSettings} />
      <div className="app-shell">
        <Sidebar items={navItems} activeView={activeView} onSelectView={setActiveView} />

        <main className="main-content">
          <TopBar
            title={viewMeta[activeView].title}
            subtitle={viewMeta[activeView].subtitle}
            searchValue={homeSearchInput}
            onSearchChange={setHomeSearchInput}
            onSearchSubmit={handleHomeSearchSubmit}
            searchDisabled={activeView !== "home"}
            hideSearch={
              activeView === "about" ||
              activeView === "queue" ||
              activeView === "discovery" ||
              activeView === "settings"
            }
          />
          <section key={activeView} className="view-transition">
            {currentView}
          </section>
          {comingSoonViews.has(activeView) ? (
            <ComingSoonOverlay
              title={`${viewMeta[activeView].title} is coming soon`}
              onBackHome={() => setActiveView("home")}
            />
          ) : null}
        </main>

        <PlayerBar />
      </div>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 2200,
          style: {
            background: "rgba(9, 15, 34, 0.95)",
            color: "#eef4ff",
            border: "1px solid rgba(133, 171, 241, 0.45)",
            boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
          },
        }}
      />
    </div>
  );
}

function App() {
  if (soundcloudConfig.HAS_MISSING_REQUIRED_PRODUCTION_ENV) {
    return <EnvMissingView />;
  }

  return (
    <PlayerProvider>
      <AppContent />
    </PlayerProvider>
  );
}

export default App;
//for push
